import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export type UserRole = "admin" | "secretary" | "doctor" | "super_admin";

export interface UserWithRole {
  id: string;
  email: string;
  full_name: string | null;
  roles: UserRole[];
  created_at: string;
}

export const useUserRoles = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: currentUserRoles = [], isLoading: isLoadingCurrentUserRoles } = useQuery({
    queryKey: ["current-user-roles"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);

      if (error) throw error;
      return data.map((r) => r.role as UserRole);
    },
  });

  const { data: allUsers = [], isLoading: isLoadingUsers, refetch: refetchUsers } = useQuery({
    queryKey: ["all-users-with-roles"],
    queryFn: async () => {
      // Buscar todos os profiles incluindo email
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name, email, created_at")
        .order("full_name");

      if (profilesError) throw profilesError;

      // Buscar roles para todos os usuários
      const { data: roles, error: rolesError } = await supabase
        .from("user_roles")
        .select("user_id, role");

      if (rolesError) throw rolesError;
      
      // Combinar dados
      const usersWithRoles: UserWithRole[] = profiles.map((profile) => {
        const userRoles = roles
          .filter((r) => r.user_id === profile.id)
          .map((r) => r.role as UserRole);

        return {
          id: profile.id,
          email: profile.email || `user-${profile.id.substring(0, 8)}`,
          full_name: profile.full_name,
          roles: userRoles,
          created_at: profile.created_at || new Date().toISOString(),
        };
      });

      return usersWithRoles;
    },
    enabled: currentUserRoles.includes("admin"),
  });

  const assignRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: UserRole }) => {
      const { error } = await supabase
        .from("user_roles")
        .insert({ user_id: userId, role });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-users-with-roles"] });
      toast({
        title: "Permissão atribuída",
        description: "A permissão foi atribuída com sucesso.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Erro ao atribuir permissão",
        description: error.message,
      });
    },
  });

  const removeRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: UserRole }) => {
      const { error } = await supabase
        .from("user_roles")
        .delete()
        .eq("user_id", userId)
        .eq("role", role);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-users-with-roles"] });
      toast({
        title: "Permissão removida",
        description: "A permissão foi removida com sucesso.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Erro ao remover permissão",
        description: error.message,
      });
    },
  });

  const isAdmin = currentUserRoles.includes("admin");
  const isSuperAdmin = currentUserRoles.includes("super_admin");
  const isSecretary = currentUserRoles.includes("secretary");
  const isDoctor = currentUserRoles.includes("doctor");

  return {
    currentUserRoles,
    isLoadingCurrentUserRoles,
    allUsers,
    isLoadingUsers,
    refetchUsers,
    assignRole: assignRoleMutation.mutate,
    removeRole: removeRoleMutation.mutate,
    isAdmin,
    isSuperAdmin,
    isSecretary,
    isDoctor,
  };
};
